import { Button, Pill, Text } from '@innovationdepartment/proxima-ui';
import { useState } from 'react';
import { SqlCodeBlock } from '../../components/FilterRules/SqlPreview';
import { DefinitionPanel, PanelSectionHeading } from '../../components/form.styles';
import { Pagination, Table, TableScroll, VisuallyHidden } from '../../components/shared.styles';
import { formatNumber, formatPercent, formatTimestamp } from '../../lib/formatters';
import type { AudienceSnapshot, SnapshotStatus } from '../../types';
import { DetailsSection, SectionTitle, SnapshotDetailCell } from './DefinitionDetails.styles';

const COLUMN_COUNT = 7;

const SNAPSHOT_STATUS: Record<
  SnapshotStatus,
  { label: string; variant: 'default' | 'info' | 'success' | 'danger' }
> = {
  queued: { label: 'Queued', variant: 'default' },
  inProgress: { label: 'Running', variant: 'info' },
  success: { label: 'Success', variant: 'success' },
  failure: { label: 'Failed', variant: 'danger' }
};

type SnapshotsTabProps = {
  snapshots: AudienceSnapshot[];
  /** Why the run history could not be loaded; shown instead of "has not run yet". */
  snapshotsError: string | null;
  snapshotsPageNumber: number;
  snapshotsPageCount: number;
  onSnapshotsPageChange: (pageNumber: number) => void;
};

/** Run history, newest first, as a table in the Deliveries tab's style, with each run's executed SQL behind a toggle. */
const SnapshotsTab = ({
  snapshots,
  snapshotsError,
  snapshotsPageNumber,
  snapshotsPageCount,
  onSnapshotsPageChange
}: SnapshotsTabProps) => (
  <DetailsSection aria-labelledby="definition-snapshots-heading">
    <SectionTitle>
      <PanelSectionHeading id="definition-snapshots-heading">Snapshots</PanelSectionHeading>
    </SectionTitle>
    {snapshotsError ? (
      <div role="alert">
        <Text variant="body2" color="red60">
          Couldn't load this audience's runs: {snapshotsError}
        </Text>
      </div>
    ) : null}
    {snapshots.length === 0 && !snapshotsError ? (
      <Text variant="body2" color="neutral60">
        This audience has not run yet.
      </Text>
    ) : null}
    {snapshots.length > 0 ? (
      <DefinitionPanel>
        <TableScroll>
          <Table>
            <thead>
              <tr>
                <th>Run</th>
                <th>Status</th>
                <th>Seed size</th>
                <th>% brand</th>
                <th>Started</th>
                <th>Completed</th>
                <th>
                  <VisuallyHidden>Executed SQL</VisuallyHidden>
                </th>
              </tr>
            </thead>
            <tbody>
              {snapshots.map(snapshot => (
                <SnapshotRows key={snapshot.id} snapshot={snapshot} />
              ))}
            </tbody>
          </Table>
        </TableScroll>
      </DefinitionPanel>
    ) : null}
    {snapshotsPageCount > 1 ? (
      <Pagination>
        <Button
          label="Newer"
          variant="text"
          size="small"
          disabled={snapshotsPageNumber <= 1}
          onClick={() => onSnapshotsPageChange(snapshotsPageNumber - 1)}
        />
        <Text variant="body2" color="neutral60">
          Page {snapshotsPageNumber} of {snapshotsPageCount}
        </Text>
        <Button
          label="Older"
          variant="text"
          size="small"
          disabled={snapshotsPageNumber >= snapshotsPageCount}
          onClick={() => onSnapshotsPageChange(snapshotsPageNumber + 1)}
        />
      </Pagination>
    ) : null}
  </DetailsSection>
);

const SnapshotRows = ({ snapshot }: { snapshot: AudienceSnapshot }) => {
  const [showSql, setShowSql] = useState(false);
  /* A status the service adds later still renders, as its raw value */
  const status = SNAPSHOT_STATUS[snapshot.status] ?? { label: snapshot.status, variant: 'default' };
  return (
    <>
      <tr>
        <td>
          <small>#{snapshot.id}</small>
        </td>
        <td>
          <Pill label={status.label} variant={status.variant} />
        </td>
        <td>{formatNumber(snapshot.seedSize)}</td>
        <td>{formatPercent(snapshot.pctBrandSeed)}</td>
        <td>{formatTimestamp(snapshot.startedAt)}</td>
        <td>{formatTimestamp(snapshot.completedAt)}</td>
        <td>
          {/* Queued, running, and failed runs carry no SQL, so they get no toggle */}
          {snapshot.executedQuery ? (
            <Button
              label={showSql ? 'Hide executed SQL' : 'Show executed SQL'}
              size="small"
              variant="text"
              onClick={() => setShowSql(previous => !previous)}
            />
          ) : null}
        </td>
      </tr>
      {snapshot.errorMessage ? (
        <tr>
          <SnapshotDetailCell colSpan={COLUMN_COUNT} error>
            {snapshot.errorMessage}
          </SnapshotDetailCell>
        </tr>
      ) : null}
      {showSql && snapshot.executedQuery ? (
        <tr>
          <SnapshotDetailCell colSpan={COLUMN_COUNT}>
            <SqlCodeBlock aria-label="Executed SQL">{snapshot.executedQuery}</SqlCodeBlock>
          </SnapshotDetailCell>
        </tr>
      ) : null}
    </>
  );
};

export default SnapshotsTab;
